/**
 * Display Engine Admin — /admin/displayengine
 * Manage clients, dynamic field schemas, items, theme, and assets.
 * Backup via backend-streamed ZIP (no frontend zip dependency).
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Monitor, Plus, Trash2, Edit, Image as ImageIcon, Palette, Save, Download,
  ExternalLink, RefreshCw, X, Sparkles, Settings as SettingsIcon, Layers, GripVertical, Calendar,
} from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '../../utils/api';
import LivePreview from '../../components/displayengine/LivePreview';

const API = process.env.REACT_APP_BACKEND_URL;

const INDUSTRIES = ['jewellery', 'food', 'retail', 'gadgets', 'fashion', 'pharmacy', 'general'];
const FIELD_TYPES = ['text', 'number', 'currency', 'image', 'url', 'rich_text', 'boolean', 'date', 'select'];

const DisplayEngineAdmin = () => {
  const [clients, setClients] = useState([]);
  const [activeClient, setActiveClient] = useState(null);
  const [activeTab, setActiveTab] = useState('items'); // items | fields | theme | assets
  const [fields, setFields] = useState([]);
  const [items, setItems] = useState([]);
  const [assets, setAssets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadClients(); loadTemplates(); }, []);
  useEffect(() => { if (activeClient) { loadFields(); loadItems(); loadAssets(); } }, [activeClient?.id]); // eslint-disable-line

  const loadClients = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/displayengine/clients');
      setClients(r.data.clients || []);
      if (r.data.clients?.length && !activeClient) setActiveClient(r.data.clients[0]);
    } catch (e) { toast.error('Failed to load clients'); }
    finally { setLoading(false); }
  };
  const loadTemplates = async () => {
    try { const r = await api.get('/displayengine/templates'); setTemplates(r.data.templates || []); } catch {}
  };
  const loadFields = async () => {
    try { const r = await api.get(`/admin/displayengine/clients/${activeClient.id}/fields`); setFields(r.data.fields || []); } catch {}
  };
  const loadItems = async () => {
    try { const r = await api.get(`/admin/displayengine/clients/${activeClient.id}/items`); setItems(r.data.items || []); } catch {}
  };
  const loadAssets = async () => {
    try { const r = await api.get(`/admin/displayengine/clients/${activeClient.id}/assets`); setAssets(r.data.assets || []); } catch {}
  };

  const seedDemo = async () => {
    if (!window.confirm('Seed 3 demo clients (Jewellery, Food, Retail) with sample fields + items?')) return;
    try {
      await api.post('/admin/displayengine/seed-demo');
      toast.success('Demo data seeded');
      loadClients();
    } catch (e) { toast.error(e.response?.data?.detail || 'Seed failed'); }
  };

  const handleDownloadZip = async () => {
    if (!activeClient) return;
    try {
      const r = await api.get(`/admin/displayengine/clients/${activeClient.id}/assets/export.zip`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `displayengine-${activeClient.slug}-backup.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
    } catch (e) { toast.error('Download failed'); }
  };

  if (loading) {
    return <div className="min-h-screen pt-28 flex items-center justify-center bg-slate-950 text-white"><div className="animate-spin w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="min-h-screen pt-28 md:pt-32 pb-12 px-4 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950 text-white" data-testid="display-engine-admin">
      <div className="max-w-7xl mx-auto">
        <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent flex items-center gap-2">
              <Monitor className="w-7 h-7 text-amber-400" /> Anant Display Engine
            </h1>
            <p className="text-slate-400 text-sm mt-1">Industry-agnostic digital signage. Custom schemas + Template Factory + Theme engine + PWA offline.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={seedDemo} className="px-3 py-2 bg-violet-500/20 hover:bg-violet-500/30 text-violet-300 rounded-lg text-sm flex items-center gap-2" data-testid="seed-demo-btn">
              <Sparkles className="w-4 h-4" /> Seed Demo Data
            </button>
            <button onClick={loadClients} className="px-3 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm flex items-center gap-2" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </header>

        <div className="grid lg:grid-cols-[280px_1fr] gap-6">
          {/* Clients sidebar */}
          <aside className="space-y-2" data-testid="client-sidebar">
            <ClientCreator onCreated={loadClients} />
            <div className="space-y-1 mt-2">
              {clients.length === 0 && <p className="text-slate-500 text-sm p-3 text-center">No clients. Seed demo data or create one.</p>}
              {clients.map(c => (
                <button key={c.id} onClick={() => setActiveClient(c)}
                  className={`w-full text-left p-3 rounded-xl transition ${activeClient?.id === c.id ? 'bg-amber-500/20 border border-amber-500/40' : 'bg-white/5 hover:bg-white/10 border border-transparent'}`}
                  data-testid={`client-${c.slug}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-bold truncate">{c.name}</span>
                    <span className="text-[10px] uppercase px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300">{c.industry}</span>
                  </div>
                  <div className="text-xs text-slate-400 font-mono mt-0.5">/{c.slug}</div>
                </button>
              ))}
            </div>
          </aside>

          {/* Main panel */}
          <main className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-6 min-h-[60vh]">
            {!activeClient ? (
              <div className="text-center py-20 text-slate-400">Select or create a client to manage.</div>
            ) : (
              <>
                <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                  <div>
                    <h2 className="text-2xl font-bold" data-testid="active-client-name">{activeClient.name}</h2>
                    <p className="text-sm text-slate-400">{activeClient.description}</p>
                  </div>
                  <div className="flex gap-2">
                    <a href={`/displayboard/${activeClient.slug}`} target="_blank" rel="noopener noreferrer"
                      className="px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 rounded-lg text-sm flex items-center gap-1.5" data-testid="open-board-btn">
                      <ExternalLink className="w-4 h-4" /> Open Board
                    </a>
                    <button onClick={handleDownloadZip} className="px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg text-sm flex items-center gap-1.5" data-testid="export-zip-btn">
                      <Download className="w-4 h-4" /> Download All (ZIP)
                    </button>
                  </div>
                </div>

                <div className="flex gap-2 mb-4 border-b border-white/10 overflow-x-auto">
                  {[
                    { id: 'items', label: 'Items', icon: Layers },
                    { id: 'fields', label: 'Schema', icon: Edit },
                    { id: 'templates', label: 'Templates', icon: Monitor },
                    { id: 'theme', label: 'Theme', icon: Palette },
                    { id: 'assets', label: 'Assets', icon: ImageIcon },
                    { id: 'translate', label: 'Translate', icon: Sparkles },
                    { id: 'settings', label: 'Settings', icon: SettingsIcon },
                  ].map(t => {
                    const Icon = t.icon;
                    return (
                      <button key={t.id} onClick={() => setActiveTab(t.id)}
                        className={`flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px whitespace-nowrap ${activeTab === t.id ? 'border-amber-400 text-amber-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
                        data-testid={`tab-${t.id}`}>
                        <Icon className="w-4 h-4" /> {t.label}
                      </button>
                    );
                  })}
                </div>

                {activeTab === 'items' && <ItemsPanel client={activeClient} fields={fields} items={items} reload={loadItems} assets={assets} />}
                {activeTab === 'fields' && <FieldsPanel client={activeClient} fields={fields} reload={loadFields} />}
                {activeTab === 'templates' && <TemplatesGallery client={activeClient} templates={templates} onApply={loadClients} />}
                {activeTab === 'theme' && <ThemePanel client={activeClient} templates={templates} onSaved={loadClients} />}
                {activeTab === 'assets' && <AssetsPanel client={activeClient} assets={assets} reload={loadAssets} />}
                {activeTab === 'translate' && <TranslatePanel client={activeClient} fields={fields} items={items} reloadAll={() => { loadFields(); loadItems(); }} />}
                {activeTab === 'settings' && <SettingsPanel client={activeClient} reload={loadClients} onDeleted={() => { setActiveClient(null); loadClients(); }} />}
              </>
            )}
          </main>
        </div>
      </div>

      {/* Live Preview floating panel — shows iframe of /displayboard/{slug} + QR code */}
      {activeClient && (
        <LivePreview
          slug={activeClient.slug}
          clientName={activeClient.name}
          themeKey={activeClient.updated_at || activeClient.created_at}
        />
      )}
    </div>
  );
};

// =================== ClientCreator ===================
const ClientCreator = ({ onCreated }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', industry: 'general', description: '' });
  const save = async () => {
    if (!form.name || !form.slug) { toast.error('Name and Slug are required'); return; }
    try {
      await api.post('/admin/displayengine/clients', form);
      toast.success('Client created');
      setForm({ name: '', slug: '', industry: 'general', description: '' });
      setAdding(false);
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  if (!adding) {
    return <button onClick={() => setAdding(true)} className="w-full py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2" data-testid="add-client-btn"><Plus className="w-4 h-4" /> New Client</button>;
  }
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-2" data-testid="add-client-form">
      <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value, slug: form.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') })}
        placeholder="Client Name" className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="client-name" />
      <input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
        placeholder="url-slug" className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm font-mono" data-testid="client-slug" />
      <select value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="client-industry">
        {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
      </select>
      <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
        placeholder="Description (optional)" rows={2} className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
      <div className="flex gap-2">
        <button onClick={() => setAdding(false)} className="flex-1 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg text-xs">Cancel</button>
        <button onClick={save} className="flex-1 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold" data-testid="save-client-btn">Save</button>
      </div>
    </div>
  );
};

// =================== FieldsPanel ===================
const FieldsPanel = ({ client, fields, reload }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ key: '', label: '', field_type: 'text', required: false, options: '', order: 0 });
  const save = async () => {
    if (!form.key || !form.label) { toast.error('Key + Label required'); return; }
    try {
      const payload = {
        client_id: client.id,
        key: form.key.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
        label: form.label,
        field_type: form.field_type,
        required: form.required,
        options: form.field_type === 'select' ? form.options.split(',').map(s => s.trim()).filter(Boolean) : null,
        order: parseInt(form.order || 0),
      };
      await api.post(`/admin/displayengine/clients/${client.id}/fields`, payload);
      toast.success('Field added');
      setForm({ key: '', label: '', field_type: 'text', required: false, options: '', order: 0 });
      setAdding(false);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete field?')) return;
    try { await api.delete(`/admin/displayengine/fields/${id}`); toast.success('Deleted'); reload(); } catch { toast.error('Failed'); }
  };
  return (
    <div className="space-y-3" data-testid="fields-panel">
      <p className="text-sm text-slate-400">Define the dynamic schema for this client. Items inherit these fields.</p>
      {!adding && <button onClick={() => setAdding(true)} className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm flex items-center gap-2" data-testid="add-field-btn"><Plus className="w-4 h-4" /> Add Field</button>}
      {adding && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-3 grid sm:grid-cols-2 gap-2" data-testid="add-field-form">
          <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value, key: form.key || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_') })} placeholder="Label (e.g. Price)" className="bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="field-label" />
          <input value={form.key} onChange={e => setForm({ ...form, key: e.target.value })} placeholder="key (snake_case)" className="bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm font-mono" data-testid="field-key" />
          <select value={form.field_type} onChange={e => setForm({ ...form, field_type: e.target.value })} className="bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="field-type">
            {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <input type="number" value={form.order} onChange={e => setForm({ ...form, order: e.target.value })} placeholder="Order" className="bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
          {form.field_type === 'select' && (
            <input value={form.options} onChange={e => setForm({ ...form, options: e.target.value })} placeholder="comma,separated,options" className="bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm sm:col-span-2" data-testid="field-options" />
          )}
          <label className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={form.required} onChange={e => setForm({ ...form, required: e.target.checked })} /> Required</label>
          <div className="flex gap-2 sm:col-span-2">
            <button onClick={() => setAdding(false)} className="flex-1 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg text-sm">Cancel</button>
            <button onClick={save} className="flex-1 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold" data-testid="save-field-btn">Save Field</button>
          </div>
        </div>
      )}
      <div className="grid gap-2">
        {fields.map(f => (
          <div key={f.id} className="bg-slate-800/50 rounded-lg p-3 flex items-center justify-between" data-testid={`field-${f.key}`}>
            <div>
              <div className="font-semibold">{f.label} <span className="text-xs text-slate-500 font-mono">({f.key})</span></div>
              <div className="text-xs text-slate-400">type: {f.field_type} · order: {f.order} {f.required && '· required'} {f.options?.length ? `· [${f.options.join(', ')}]` : ''}</div>
            </div>
            <button onClick={() => del(f.id)} className="p-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg" data-testid={`del-field-${f.key}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

// =================== ItemsPanel ===================
const ItemsPanel = ({ client, fields, items, reload, assets }) => {
  const [editing, setEditing] = useState(null);
  const [localItems, setLocalItems] = useState(items);
  useEffect(() => { setLocalItems(items); }, [items]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const blankData = () => fields.reduce((acc, f) => { acc[f.key] = f.field_type === 'boolean' ? false : ''; return acc; }, {});
  const blank = () => ({ title: '', category: '', data: blankData(), image_urls: [], is_active: true, order: items.length, active_from: '', active_to: '' });

  const save = async () => {
    if (!editing.title) { toast.error('Title required'); return; }
    try {
      const payload = { ...editing };
      // Convert empty schedule strings to null
      payload.active_from = payload.active_from || null;
      payload.active_to = payload.active_to || null;
      if (editing.id) {
        await api.put(`/admin/displayengine/items/${editing.id}`, payload);
      } else {
        await api.post(`/admin/displayengine/clients/${client.id}/items`, { ...payload, client_id: client.id });
      }
      toast.success('Saved');
      setEditing(null);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete item?')) return;
    try { await api.delete(`/admin/displayengine/items/${id}`); toast.success('Deleted'); reload(); } catch { toast.error('Failed'); }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = localItems.findIndex(i => i.id === active.id);
    const newIndex = localItems.findIndex(i => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(localItems, oldIndex, newIndex);
    setLocalItems(reordered);
    // Persist
    const ordering = reordered.map((it, idx) => ({ id: it.id, order: idx }));
    try {
      await api.put(`/admin/displayengine/clients/${client.id}/items/reorder`, { ordering });
      toast.success('Order saved');
    } catch (e) { toast.error('Reorder failed'); reload(); }
  };

  return (
    <div className="space-y-3" data-testid="items-panel">
      {fields.length === 0 ? (
        <p className="text-amber-300 text-sm bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">⚠ Add at least one field in the Schema tab before creating items.</p>
      ) : !editing ? (
        <div className="flex items-center gap-2">
          <button onClick={() => setEditing(blank())} className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm flex items-center gap-2" data-testid="add-item-btn"><Plus className="w-4 h-4" /> Add Item</button>
          <span className="text-xs text-slate-500">Drag <GripVertical className="w-3 h-3 inline" /> handle to reorder · 🗓 schedule window enforces visibility</span>
        </div>
      ) : null}

      {editing && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3" data-testid="item-editor">
          <div className="grid sm:grid-cols-3 gap-2">
            <input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} placeholder="Title" className="bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm sm:col-span-2" data-testid="item-title" />
            <input value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })} placeholder="Category (optional)" className="bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="item-category" />
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {fields.map(f => (
              <div key={f.id}>
                <label className="block text-xs text-slate-400 mb-1">{f.label} {f.required && <span className="text-red-400">*</span>}</label>
                {f.field_type === 'select' ? (
                  <select value={editing.data?.[f.key] || ''} onChange={e => setEditing({ ...editing, data: { ...editing.data, [f.key]: e.target.value } })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid={`item-data-${f.key}`}>
                    <option value="">--</option>
                    {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.field_type === 'boolean' ? (
                  <input type="checkbox" checked={!!editing.data?.[f.key]} onChange={e => setEditing({ ...editing, data: { ...editing.data, [f.key]: e.target.checked } })} className="w-4 h-4" data-testid={`item-data-${f.key}`} />
                ) : f.field_type === 'number' || f.field_type === 'currency' ? (
                  <input type="number" step="0.01" value={editing.data?.[f.key] ?? ''} onChange={e => setEditing({ ...editing, data: { ...editing.data, [f.key]: e.target.value === '' ? '' : parseFloat(e.target.value) } })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid={`item-data-${f.key}`} />
                ) : (
                  <input value={editing.data?.[f.key] ?? ''} onChange={e => setEditing({ ...editing, data: { ...editing.data, [f.key]: e.target.value } })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid={`item-data-${f.key}`} />
                )}
              </div>
            ))}
          </div>
          {/* Scheduling */}
          <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-white/10">
            <div>
              <label className="flex items-center gap-1 text-xs text-slate-400 mb-1"><Calendar className="w-3 h-3" /> Active From (optional)</label>
              <input type="datetime-local" value={editing.active_from || ''} onChange={e => setEditing({ ...editing, active_from: e.target.value ? new Date(e.target.value).toISOString() : '' })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="item-active-from" />
            </div>
            <div>
              <label className="flex items-center gap-1 text-xs text-slate-400 mb-1"><Calendar className="w-3 h-3" /> Active To (optional)</label>
              <input type="datetime-local" value={editing.active_to || ''} onChange={e => setEditing({ ...editing, active_to: e.target.value ? new Date(e.target.value).toISOString() : '' })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="item-active-to" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Image URL (paste a URL or pick from Assets tab)</label>
            <input value={editing.image_urls?.[0] || ''} onChange={e => setEditing({ ...editing, image_urls: [e.target.value] })} placeholder="https://..." className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="item-image-url" />
            {assets?.length > 0 && (
              <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                {assets.slice(0, 12).map(a => (
                  <button key={a.id} onClick={() => setEditing({ ...editing, image_urls: [API + a.url] })} className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border border-white/10 hover:border-amber-500" title={a.original_filename}>
                    <img src={API + a.url} alt={a.original_filename} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setEditing(null)} className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg text-sm">Cancel</button>
            <button onClick={save} className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-semibold flex items-center gap-1" data-testid="save-item-btn"><Save className="w-4 h-4" /> Save Item</button>
          </div>
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={localItems.map(i => i.id)} strategy={rectSortingStrategy}>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="items-list">
            {localItems.map(it => (
              <SortableItemCard key={it.id} item={it} onEdit={() => setEditing(it)} onDelete={() => del(it.id)} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

// =================== TemplatesGallery ===================
const TemplatesGallery = ({ client, templates, onApply }) => {
  const currentId = client.theme?.template_id || 'grid';
  const apply = async (templateId) => {
    if (!window.confirm(`Apply template "${templateId}" to ${client.name}?`)) return;
    try {
      await api.put(`/admin/displayengine/clients/${client.id}/theme`, { template_id: templateId });
      toast.success(`✓ Template "${templateId}" applied`);
      onApply();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  // Visual preview per template (simple CSS mockups)
  const previews = {
    grid: (
      <div className="grid grid-cols-3 gap-1 p-2">
        {[...Array(6)].map((_, i) => <div key={i} className="bg-gradient-to-br from-amber-400/40 to-orange-500/40 rounded aspect-square" />)}
      </div>
    ),
    promo_banner: (
      <div className="relative h-full bg-gradient-to-br from-pink-500/40 to-purple-500/40 flex items-center justify-center">
        <div className="text-center">
          <div className="h-2 w-10 bg-white/40 rounded mb-1 mx-auto" />
          <div className="h-4 w-24 bg-white/70 rounded mb-1" />
          <div className="h-3 w-16 bg-amber-400 rounded mx-auto" />
        </div>
      </div>
    ),
    menu_list: (
      <div className="p-2 space-y-1.5">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex justify-between">
            <div className="h-2 w-16 bg-white/50 rounded" />
            <div className="h-2 w-8 bg-amber-400 rounded" />
          </div>
        ))}
      </div>
    ),
    minimalist_card: (
      <div className="p-3 h-full flex gap-2 items-center">
        <div className="aspect-square h-16 bg-amber-500/30 rounded-lg" />
        <div className="flex-1 space-y-1.5">
          <div className="h-2 w-10 bg-white/40 rounded" />
          <div className="h-3 w-16 bg-white/70 rounded" />
          <div className="h-4 w-12 bg-amber-400 rounded" />
        </div>
      </div>
    ),
    split_screen: (
      <div className="grid grid-cols-2 h-full">
        <div className="bg-gradient-to-br from-amber-500/40 to-orange-500/40 flex items-center justify-center">
          <div className="h-4 w-12 bg-white/70 rounded" />
        </div>
        <div className="grid grid-cols-2 gap-1 p-1.5">
          {[...Array(4)].map((_, i) => <div key={i} className="bg-amber-400/40 rounded" />)}
        </div>
      </div>
    ),
    weather_widget: (
      <div className="relative h-full">
        <div className="grid grid-cols-3 gap-1 p-2 h-full">
          {[...Array(6)].map((_, i) => <div key={i} className="bg-gradient-to-br from-blue-500/30 to-cyan-500/30 rounded" />)}
        </div>
        <div className="absolute top-1.5 right-1.5 bg-slate-900/80 rounded-lg p-1.5 border border-blue-400/40">
          <div className="text-base leading-none">☀️</div>
          <div className="text-[8px] font-bold text-blue-300">28°C</div>
        </div>
      </div>
    ),
    gold_rate_board: (
      <div className="h-full p-2" style={{ background: 'radial-gradient(ellipse at top, #2a1a08, #0a0604)' }}>
        <div className="text-center text-[9px] font-extrabold tracking-widest mb-1" style={{ color: '#d4a648' }}>GOLD RATE</div>
        <div className="grid grid-cols-2 gap-1 mb-1">
          <div className="bg-black rounded px-1 py-0.5 text-center text-[9px] font-bold" style={{ color: '#ff3a1c', fontFamily: 'monospace', textShadow: '0 0 4px #ff3a1c' }}>24.06.26</div>
          <div className="bg-black rounded px-1 py-0.5 text-center text-[9px] font-bold" style={{ color: '#ff3a1c', fontFamily: 'monospace', textShadow: '0 0 4px #ff3a1c' }}>10:30:00</div>
        </div>
        {['24 CT', '22 CT', 'SILVER'].map((label, i) => (
          <div key={i} className="grid grid-cols-2 gap-1 mb-0.5">
            <div className="text-[8px] font-bold py-0.5" style={{ color: '#e8d8b0' }}>{label}</div>
            <div className="bg-black rounded text-center text-[8px] font-bold" style={{ color: '#ff3a1c', fontFamily: 'monospace', textShadow: '0 0 4px #ff3a1c' }}>{['143300', '136800', '002300'][i]}</div>
          </div>
        ))}
      </div>
    ),
  };
  return (
    <div className="space-y-4" data-testid="templates-gallery">
      <p className="text-sm text-slate-400">Choose a layout for <b className="text-white">{client.name}</b>. Click any card to apply instantly — Live Preview will refresh.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map(t => {
          const isActive = t.id === currentId;
          return (
            <button key={t.id} onClick={() => apply(t.id)} data-testid={`template-card-${t.id}`}
              className={`text-left rounded-2xl border-2 transition overflow-hidden ${isActive ? 'border-amber-400 ring-2 ring-amber-400/30' : 'border-white/10 hover:border-amber-400/50'}`}
              style={{ backgroundColor: '#0f172a' }}>
              {/* Preview pane */}
              <div className="h-32 bg-slate-800/60 border-b border-white/10">{previews[t.id] || previews.grid}</div>
              <div className="p-3">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="font-bold text-white">{t.name}</h4>
                  {isActive && <span className="text-[10px] px-2 py-0.5 bg-emerald-500/20 text-emerald-300 rounded-full">ACTIVE</span>}
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">{t.description}</p>
                <div className="flex gap-1 mt-2 flex-wrap">
                  {(t.industries || []).map(ind => (
                    <span key={ind} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">{ind}</span>
                  ))}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// =================== TranslatePanel (Claude Sonnet auto-translate) ===================
const TranslatePanel = ({ client, fields, items, reloadAll }) => {
  const [lang, setLang] = useState('Hindi');
  const [scope, setScope] = useState('both');
  const [existing, setExisting] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const LANGS = ['Hindi', 'Gujarati', 'Marathi', 'Tamil', 'Telugu', 'Bengali', 'Punjabi', 'Kannada', 'Malayalam', 'Urdu', 'Spanish', 'French', 'Arabic'];

  const loadExisting = async () => {
    try { const r = await api.get(`/admin/displayengine/clients/${client.id}/translations`); setExisting(r.data.languages || []); } catch {}
  };
  useEffect(() => { loadExisting(); }, [client.id]); // eslint-disable-line

  const translate = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await api.post(`/admin/displayengine/clients/${client.id}/translate`, { target_language: lang, scope });
      setResult(r.data);
      toast.success(`✓ Translated ${r.data.translated} strings to ${lang}`);
      loadExisting();
    } catch (e) { toast.error(e.response?.data?.detail || 'Translation failed'); }
    finally { setBusy(false); }
  };
  const deleteLang = async (l) => {
    if (!window.confirm(`Delete ${l} translation cache?`)) return;
    try { await api.delete(`/admin/displayengine/clients/${client.id}/translations/${encodeURIComponent(l)}`); toast.success('Deleted'); loadExisting(); } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="translate-panel">
      <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-4">
        <h3 className="font-bold text-violet-300 flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4" /> Auto-Translate Display Board</h3>
        <p className="text-sm text-slate-300">One-click translate all field labels + item titles/descriptions using <b>Claude Sonnet</b>. Translations are cached — public board reader can switch languages on the fly. Brand names + numbers stay unchanged.</p>
        <p className="text-xs text-slate-500 mt-1">Source: <b>{fields.length}</b> fields + <b>{items.length}</b> items.</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs text-slate-400 mb-1">Target Language</label>
          <select value={lang} onChange={e => setLang(e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="translate-lang">
            {LANGS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Scope</label>
          <select value={scope} onChange={e => setScope(e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="translate-scope">
            <option value="both">Fields + Items</option>
            <option value="items">Items only</option>
            <option value="fields">Fields only</option>
          </select>
        </div>
        <div className="flex items-end">
          <button onClick={translate} disabled={busy} className="w-full px-3 py-1.5 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-1" data-testid="translate-run">
            {busy ? <><RefreshCw className="w-4 h-4 animate-spin" /> Translating…</> : <><Sparkles className="w-4 h-4" /> Translate to {lang}</>}
          </button>
        </div>
      </div>
      {result && (
        <div className="bg-slate-800/60 rounded-xl p-3" data-testid="translate-result">
          <div className="text-xs text-slate-400 mb-2">✓ Translated {result.translated} strings · Sample:</div>
          <div className="text-xs space-y-0.5 max-h-40 overflow-y-auto font-mono">
            {Object.entries(result.preview || {}).map(([k, v]) => (
              <div key={k}><span className="text-slate-500">{k.split(':').pop()}:</span> <span className="text-amber-300">{v}</span></div>
            ))}
          </div>
        </div>
      )}
      {existing.length > 0 && (
        <div>
          <h4 className="text-sm font-bold text-slate-300 mb-2">Cached translations</h4>
          <div className="flex flex-wrap gap-2">
            {existing.map(e => (
              <div key={e.language} className="px-2 py-1 bg-emerald-500/15 border border-emerald-500/30 rounded-lg text-xs flex items-center gap-2" data-testid={`lang-${e.language}`}>
                <span className="font-semibold text-emerald-300">{e.language}</span>
                <span className="text-slate-400">{e.count} strings</span>
                <button onClick={() => deleteLang(e.language)} className="text-red-400 hover:text-red-300" data-testid={`del-lang-${e.language}`}><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SortableItemCard = ({ item, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 10 : 'auto' };
  const now = new Date();
  const af = item.active_from ? new Date(item.active_from) : null;
  const at = item.active_to ? new Date(item.active_to) : null;
  const inWindow = (!af || now >= af) && (!at || now <= at);
  return (
    <div ref={setNodeRef} style={style} className="bg-slate-800/50 rounded-xl border border-white/10 overflow-hidden" data-testid={`item-row-${item.id}`}>
      {item.image_urls?.[0] && <img src={item.image_urls[0]} alt={item.title} className="w-full h-32 object-cover" />}
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <button {...attributes} {...listeners} className="text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing pt-0.5" title="Drag to reorder" data-testid={`drag-${item.id}`}>
              <GripVertical className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <div className="font-bold text-sm truncate">{item.title}</div>
              {item.category && <div className="text-xs text-slate-400 truncate">{item.category}</div>}
              {(af || at) && (
                <div className={`text-[10px] mt-1 flex items-center gap-1 ${inWindow ? 'text-emerald-300' : 'text-amber-300'}`} data-testid={`schedule-${item.id}`}>
                  <Calendar className="w-3 h-3" />
                  {af && `From ${new Date(af).toLocaleString()}`}
                  {af && at && ' → '}
                  {at && `To ${new Date(at).toLocaleString()}`}
                  {!inWindow && ' (hidden now)'}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            <button onClick={onEdit} className="p-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded" data-testid={`edit-item-${item.id}`}><Edit className="w-3.5 h-3.5" /></button>
            <button onClick={onDelete} className="p-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded" data-testid={`del-item-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =================== ThemePanel ===================
const ThemePanel = ({ client, templates, onSaved }) => {
  const [theme, setTheme] = useState(client.theme || {});
  useEffect(() => { setTheme(client.theme || {}); }, [client.id]); // eslint-disable-line
  const save = async () => {
    try {
      await api.put(`/admin/displayengine/clients/${client.id}/theme`, theme);
      toast.success('Theme saved (live boards update instantly)');
      onSaved();
    } catch (e) { toast.error('Failed'); }
  };
  const setF = (k, v) => setTheme({ ...theme, [k]: v });
  return (
    <div className="space-y-4" data-testid="theme-panel">
      <div>
        <label className="block text-xs text-slate-400 mb-1">Template</label>
        <select value={theme.template_id || 'grid'} onChange={e => setF('template_id', e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="theme-template">
          {templates.map(t => <option key={t.id} value={t.id}>{t.name} — {t.description}</option>)}
        </select>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {['primary_color', 'secondary_color', 'background_color', 'text_color', 'accent_color'].map(k => (
          <div key={k}>
            <label className="block text-xs text-slate-400 mb-1 capitalize">{k.replace(/_/g, ' ')}</label>
            <div className="flex gap-2">
              <input type="color" value={theme[k] || '#000000'} onChange={e => setF(k, e.target.value)} className="w-10 h-9 bg-slate-800 rounded border border-white/10" data-testid={`color-${k}`} />
              <input value={theme[k] || ''} onChange={e => setF(k, e.target.value)} className="flex-1 bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm font-mono" />
            </div>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-slate-400 mb-1">Body Font</label>
          <input value={theme.font_family || ''} onChange={e => setF('font_family', e.target.value)} placeholder="Inter" className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="font-family" />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Heading Font</label>
          <input value={theme.heading_font || ''} onChange={e => setF('heading_font', e.target.value)} placeholder="Playfair Display" className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Logo URL</label>
          <input value={theme.logo_url || ''} onChange={e => setF('logo_url', e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Rotation seconds (slideshows)</label>
          <input type="number" value={theme.rotation_seconds || 8} onChange={e => setF('rotation_seconds', parseInt(e.target.value || 8))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!theme.show_ticker} onChange={e => setF('show_ticker', e.target.checked)} data-testid="show-ticker" /> Show scrolling ticker bar</label>
      {theme.show_ticker && (
        <input value={theme.ticker_text || ''} onChange={e => setF('ticker_text', e.target.value)} placeholder="Today's offer text…" className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="ticker-text" />
      )}
      {/* Weather widget config (only meaningful if template_id === 'weather_widget') */}
      {theme.template_id === 'weather_widget' && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 space-y-2" data-testid="weather-config">
          <h4 className="font-bold text-sm text-blue-300">🌤 Weather Widget Config</h4>
          <div className="grid sm:grid-cols-2 gap-2">
            <input value={theme.weather_city || ''} onChange={e => setF('weather_city', e.target.value)}
              placeholder="City (e.g., New Delhi) — leave empty to auto-detect from client industry"
              className="bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="weather-city" />
            <select value={theme.weather_unit || 'C'} onChange={e => setF('weather_unit', e.target.value)} className="bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="weather-unit">
              <option value="C">°C (Celsius)</option>
              <option value="F">°F (Fahrenheit)</option>
            </select>
          </div>
          <p className="text-xs text-slate-500">Live weather from free Open-Meteo API · refreshes every 10 min</p>
        </div>
      )}
      {/* Gold Rate Board config (only meaningful if template_id === 'gold_rate_board') */}
      {theme.template_id === 'gold_rate_board' && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 space-y-2" data-testid="gold-rate-config">
          <h4 className="font-bold text-sm text-amber-300">🏆 Gold Rate Board Config</h4>
          <div className="grid sm:grid-cols-2 gap-2">
            <input value={theme.board_title || ''} onChange={e => setF('board_title', e.target.value)}
              placeholder='Board Title (default: "GOLD RATE")'
              className="bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="gold-board-title" />
            <input value={theme.rate_label || ''} onChange={e => setF('rate_label', e.target.value)}
              placeholder="Rate label (default: Today's RATE 10 Per gram)"
              className="bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="gold-rate-label" />
          </div>
          <input value={theme.footer_text || ''} onChange={e => setF('footer_text', e.target.value)}
            placeholder='Footer (default: "MAKING & GST CHARGES EXTRA")'
            className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" data-testid="gold-footer-text" />
          <p className="text-xs text-slate-500">
            Add items with title <b>{'"24 CT", "22 CT", "18 CT", "SILVER"'}</b>. The first <i>currency</i> field will be rendered as a 6-digit LED.
          </p>
        </div>
      )}
      <button onClick={save} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2" data-testid="save-theme-btn"><Save className="w-4 h-4" /> Save Theme</button>
    </div>
  );
};

// =================== AssetsPanel ===================
const AssetsPanel = ({ client, assets, reload }) => {
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', 'item_image');
      try {
        await api.post(`/admin/displayengine/clients/${client.id}/assets`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch (err) { toast.error(`Upload failed: ${file.name}`); }
    }
    setUploading(false);
    toast.success(`Uploaded ${files.length} file(s)`);
    reload();
  };
  const del = async (id) => {
    if (!window.confirm('Delete asset?')) return;
    try { await api.delete(`/admin/displayengine/assets/${id}`); toast.success('Deleted'); reload(); } catch { toast.error('Failed'); }
  };
  return (
    <div className="space-y-4" data-testid="assets-panel">
      <label className="block">
        <span className="text-sm text-slate-400 mb-2 block">Drop or select images (JPG/PNG/WEBP/SVG):</span>
        <input type="file" accept="image/*" multiple onChange={handleUpload} className="block w-full text-sm text-slate-300 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-amber-500 file:text-white file:cursor-pointer hover:file:bg-amber-600" data-testid="upload-input" />
      </label>
      {uploading && <p className="text-amber-400 text-sm">Uploading…</p>}
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="assets-grid">
        {assets.map(a => (
          <div key={a.id} className="relative group rounded-lg overflow-hidden border border-white/10 aspect-square" data-testid={`asset-${a.id}`}>
            <img src={API + a.url} alt={a.original_filename} className="w-full h-full object-cover" />
            <button onClick={() => del(a.id)} className="absolute top-1 right-1 p-1 bg-red-500 hover:bg-red-600 text-white rounded opacity-0 group-hover:opacity-100 transition" data-testid={`del-asset-${a.id}`}><X className="w-3 h-3" /></button>
            <div className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-[10px] p-1 truncate">{a.original_filename}</div>
          </div>
        ))}
      </div>
      {assets.length === 0 && <p className="text-slate-500 text-sm text-center py-8">No assets yet.</p>}
    </div>
  );
};

// =================== SettingsPanel ===================
const SettingsPanel = ({ client, reload, onDeleted }) => {
  const [form, setForm] = useState(client);
  useEffect(() => { setForm(client); }, [client.id]); // eslint-disable-line
  const save = async () => {
    try {
      await api.put(`/admin/displayengine/clients/${client.id}`, {
        name: form.name, description: form.description, industry: form.industry,
        contact_email: form.contact_email, contact_phone: form.contact_phone, is_active: form.is_active,
      });
      toast.success('Saved');
      reload();
    } catch (e) { toast.error('Failed'); }
  };
  const del = async () => {
    if (!window.confirm(`Delete "${client.name}" and ALL its items + fields permanently?`)) return;
    try { await api.delete(`/admin/displayengine/clients/${client.id}`); toast.success('Deleted'); onDeleted(); } catch { toast.error('Failed'); }
  };
  return (
    <div className="space-y-3" data-testid="settings-panel">
      <input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Name" className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
      <input value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description" className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
      <select value={form.industry || 'general'} onChange={e => setForm({ ...form, industry: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm">
        {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
      </select>
      <input value={form.contact_email || ''} onChange={e => setForm({ ...form, contact_email: e.target.value })} placeholder="Contact Email" className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
      <input value={form.contact_phone || ''} onChange={e => setForm({ ...form, contact_phone: e.target.value })} placeholder="Contact Phone" className="w-full bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} /> Active (visible on public board)</label>
      <div className="flex gap-2 justify-between pt-2">
        <button onClick={del} className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg text-sm flex items-center gap-1" data-testid="delete-client-btn"><Trash2 className="w-4 h-4" /> Delete Client</button>
        <button onClick={save} className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-semibold flex items-center gap-1" data-testid="save-settings-btn"><Save className="w-4 h-4" /> Save</button>
      </div>
    </div>
  );
};

export default DisplayEngineAdmin;
